// CP-8515: forward the chat to any email address (conversation menu › "Forward by email").
//
// Backend: GET/POST chat/conversation_forward/<unique_name>/. GET lists the messages to pick from (all ticked; a
// promotion or an automated reply can be left out), the guest's details the email carries and the Reply-To (the
// operator's primary email). POST sends {to, note, exclude_message_ids}; the backend leaves a staff note in the chat.
import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { CREATE, DataAPIContext, GET_ONE } from '../api';
import { cv } from '../themeVars';

const RESOURCE = 'chat/conversation_forward';
const NOT_FROM_PEOPLE = ['automated', 'campaign'];

const KIND_LABEL = { automated: 'automated', campaign: 'campaign' };

function guestLine(g) {
    const name = [g.first_name, g.last_name].filter(Boolean).join(' ');
    return [name || 'Guest', g.email, g.phone].filter(Boolean).join(' · ');
}

export default function ForwardConversationDialog({ open, onClose, conversationId }) {
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const [preview, setPreview] = React.useState(null);
    const [loadError, setLoadError] = React.useState('');
    const [to, setTo] = React.useState('');
    const [note, setNote] = React.useState('');
    const [excluded, setExcluded] = React.useState(() => new Set());
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (!open || !conversationId) return undefined;
        let live = true;
        setPreview(null);
        setLoadError('');
        setError('');
        setExcluded(new Set());
        dataAPI(GET_ONE, RESOURCE, { id: conversationId })
            .then((res) => {
                if (!live) return;
                if (res?.statusCode === 200) setPreview(res.data);
                else setLoadError(res?.data?.message || "This conversation can't be forwarded.");
            })
            .catch(() => live && setLoadError("The conversation couldn't be loaded. Please try again."));
        return () => {
            live = false;
        };
    }, [dataAPI, open, conversationId]);

    const messages = preview?.messages || [];
    const selectedCount = messages.length - messages.filter((m) => excluded.has(m.id)).length;
    const hasNotFromPeople = messages.some((m) => NOT_FROM_PEOPLE.includes(m.kind));

    const toggle = (id) => {
        setExcluded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const leaveOutAutomated = () => {
        setExcluded((prev) => new Set([...prev, ...messages.filter((m) => NOT_FROM_PEOPLE.includes(m.kind)).map((m) => m.id)]));
    };

    const close = () => {
        if (busy) return;
        onClose();
    };

    const send = async () => {
        if (!to.trim()) {
            setError('Enter an email address to forward to.');
            return;
        }
        setBusy(true);
        setError('');
        const res = await dataAPI(CREATE, RESOURCE, {
            urlSuffix: `${encodeURIComponent(conversationId)}/`,
            data: { to, note, exclude_message_ids: [...excluded] },
        });
        setBusy(false);
        if (res?.statusCode !== 200) {
            setError(res?.data?.message || "The conversation couldn't be forwarded. Please try again.");
            return;
        }
        const { sent_to: sentTo = [], failed = [] } = res.data || {};
        enqueueSnackbar(`Conversation forwarded to ${sentTo.join(', ')}`, { variant: 'success' });
        if (failed.length) {
            enqueueSnackbar(`Couldn't send to ${failed.join(', ')}`, { variant: 'warning', autoHideDuration: 10000 });
        }
        setTo('');
        setNote('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={close} maxWidth="sm" fullWidth aria-labelledby="forward-conversation-title">
            <DialogTitle id="forward-conversation-title">Forward conversation by email</DialogTitle>
            <DialogContent>
                {!preview && !loadError && (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={28} />
                    </Box>
                )}
                {loadError && <Alert severity="error">{loadError}</Alert>}
                {preview && (
                    <Box display="flex" flexDirection="column" gap={2} pt={1}>
                        <TextField
                            id="forward-conversation-to"
                            label="To"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                            placeholder="name@example.com"
                            helperText={`Up to ${preview.max_recipients || 5} addresses, separated by commas.`}
                            autoFocus
                            fullWidth
                        />
                        <TextField
                            id="forward-conversation-note"
                            label="Note at the top (optional)"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            multiline
                            minRows={2}
                            inputProps={{ maxLength: preview.max_note_chars || 2000 }}
                            fullWidth
                        />
                        <Box sx={{ color: 'text.secondary', typography: 'body2' }}>
                            {(preview.guests || []).length > 0 && (
                                <div>{`Guest details included: ${preview.guests.map(guestLine).join('; ')}`}</div>
                            )}
                            <div>Your team's messages show each sender's name and email.</div>
                            {preview.reply_to && <div>Replies go to {preview.reply_to}.</div>}
                        </Box>
                        <Box>
                            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                                <Typography variant="subtitle2">
                                    {`Messages to include (${selectedCount} of ${messages.length})`}
                                </Typography>
                                <Box display="flex" gap={1}>
                                    {hasNotFromPeople && (
                                        <Button size="small" onClick={leaveOutAutomated}>
                                            Leave out automated and campaign messages
                                        </Button>
                                    )}
                                    {excluded.size > 0 && (
                                        <Button size="small" onClick={() => setExcluded(new Set())}>
                                            Include all
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                            {preview.omitted > 0 && (
                                <Typography variant="caption" color="text.secondary" component="div">
                                    {`Only the latest ${messages.length} messages can be forwarded; ${preview.omitted} earlier ones aren't included.`}
                                </Typography>
                            )}
                            <Box
                                sx={{
                                    maxHeight: 320,
                                    overflowY: 'auto',
                                    border: `1px solid ${cv('border', 'rgb(224, 224, 224)')}`,
                                    borderRadius: 1,
                                    mt: 1,
                                }}
                            >
                                {messages.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                        This conversation has no messages to forward.
                                    </Typography>
                                )}
                                {messages.map((m) => {
                                    const included = !excluded.has(m.id);
                                    return (
                                        <Box
                                            key={m.id}
                                            component="label"
                                            htmlFor={`forward-message-${m.id}`}
                                            display="flex"
                                            alignItems="flex-start"
                                            gap={0.5}
                                            sx={{
                                                px: 1,
                                                py: 0.5,
                                                cursor: 'pointer',
                                                opacity: included ? 1 : 0.5,
                                                borderBottom: `1px solid ${cv('border', 'rgb(240, 240, 240)')}`,
                                            }}
                                        >
                                            <Checkbox
                                                id={`forward-message-${m.id}`}
                                                size="small"
                                                checked={included}
                                                onChange={() => toggle(m.id)}
                                                sx={{ p: 0.5 }}
                                            />
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography variant="caption" color="text.secondary" component="div">
                                                    <b>{m.who}</b>
                                                    {m.email ? ` (${m.email})` : ''}
                                                    {KIND_LABEL[m.kind] ? ` · ${KIND_LABEL[m.kind]}` : ''}
                                                    {m.time ? ` · ${m.time}` : ''}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 3,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {m.text}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            A staff note in this chat records the forward. The guest isn't notified.
                        </Typography>
                        {error && <Alert severity="error">{error}</Alert>}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={busy}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={send} disabled={busy || !preview || selectedCount === 0}>
                    {busy ? 'Sending…' : 'Forward'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
